import { FormStore, GlobalStore } from "@roc/feature-app-core";
import { action, flow, makeObservable, observable } from "mobx";
import { PayoffService } from "../services/payoffService";
import { LoanStore } from "@roc/feature-loans";
import { DocumentStore } from "@roc/feature-documents";

const PENDING = "Pending";
const APPROVED = "Approved";
const REJECTED = "Rejected";

export class PayoffReconciliationReviewStore {
  private globalStore: GlobalStore;
  private documentStore: DocumentStore;
  private loanStore: LoanStore;
  private payoffService: PayoffService;

  isRequestResolved: boolean;
  goodThroughDate: string;
  reserveFees: number;
  toChargesPrincipal: number;
  waiverAmount: number;
  waiverDescription: string;
  payoffComments: string;
  fundedAmount: number;
  totalExtensionFee: number;
  prepaymentPenalty: number;
  otherFeesAdjustment: number;
  otherFeesCategories: string;
  sectionTotals: Array<any>;
  payoffWaiverRequestId: number;
  payoffId: number;
  loanId: number;
  status: string;
  waiverRequest: any;
  interestWaiverItems: Array<any>;
  servicerFeeWaiverItems: Array<any>;
  unpaidChargeWaiverItems: Array<any>;
  finalTotal: number;

  constructor(loanStore: LoanStore, documentStore: DocumentStore, globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.documentStore = documentStore;
    this.loanStore = loanStore;
    this.payoffService = new PayoffService();
    this.setDefaults();
    makeObservable(this, {
      isRequestResolved: observable,
      goodThroughDate: observable,
      reserveFees: observable,
      toChargesPrincipal: observable,
      waiverAmount: observable,
      waiverDescription: observable,
      payoffComments: observable,
      fundedAmount: observable,
      totalExtensionFee: observable,
      prepaymentPenalty: observable,
      otherFeesAdjustment: observable,
      otherFeesCategories: observable,
      payoffWaiverRequestId: observable,
      payoffId: observable,
      loanId: observable,
      status: observable,
      waiverRequest: observable,
      interestWaiverItems: observable,
      servicerFeeWaiverItems: observable,
      unpaidChargeWaiverItems: observable,
      finalTotal: observable,
      sectionTotals: observable,
      initialize: flow,
      getPayoffWaiverRequest: flow,
      updateWaiverItem: flow,
      approveWaiverItem: flow,
      rejectWaiverItem: flow,
      submitWaiverRequest: flow,
      markRequestAsResolved: flow,
      setDisplayFields: action,
      setInterest: action,
      setServicerFees: action,
      setUnpaidCharges: action,
      calculateFinalTotal: action,
      setFinalTotals: action,
      getPayoffAmount: action,
      getListTotal: action,
    });
  }

  setDefaults() {
    this.isRequestResolved = false;
    this.goodThroughDate = '';
    this.reserveFees = 0;
    this.toChargesPrincipal = 0;
    this.waiverAmount = 0;
    this.waiverDescription = '';
    this.payoffComments = '';
    this.fundedAmount = 0;
    this.totalExtensionFee = 0;
    this.prepaymentPenalty = 0;
    this.otherFeesAdjustment = 0;
    this.otherFeesCategories = '';
    this.sectionTotals = [];
    this.payoffWaiverRequestId = null;
    this.payoffId = null;
    this.loanId = null;
    this.status = '';
    this.waiverRequest = null;
    this.interestWaiverItems = [];
    this.servicerFeeWaiverItems = [];
    this.unpaidChargeWaiverItems = [];
    this.finalTotal = 0;
  }

  reset() {
    this.setDefaults();
  }

  *initialize() {
    this.reset();
    this.loanId = this.loanStore?.loanDetails?.loanId;
    this.payoffId = this.documentStore?.currentDocument?.payoffId;
    this.getPayoffWaiverRequest();
  }

  *getPayoffWaiverRequest() {
    try {
      const response = yield this.payoffService.getActivePayoffWaiverRequest(this.loanId, this.payoffId);
      this.waiverRequest = response?.data?.data;
      this.isRequestResolved = this.waiverRequest?.status === APPROVED;
      this.setDisplayFields(this.waiverRequest);
      this.setInterest(this.waiverRequest?.waiverItems);
      this.setServicerFees(this.waiverRequest?.waiverItems);
      this.setUnpaidCharges(this.waiverRequest?.waiverItems);
      this.calculateFinalTotal();
      this.setFinalTotals();
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while getting payoff waiver request',
      });
    }
  }

  *updateWaiverItem(waiverItem) {
    try {
      waiverItem.status = PENDING;
      const foundIndex = this.waiverRequest.waiverItems.findIndex(item => item.name === waiverItem.name && item.section === waiverItem.section);
      if (foundIndex !== -1) {
        this.waiverRequest.waiverItems[foundIndex] = waiverItem;
      }
      yield this.payoffService.savePayoffWaiverRequest(this.waiverRequest);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Waiver item has been updated',
      });
      yield this.initialize();
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while updating waiver',
      });
    }
  }

  *approveWaiverItem(waiverItem) {
    try {
      yield this.payoffService.approvePayoffWaiverItem(this.loanId, this.payoffId, waiverItem.payoffWaiverItemId);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Waiver item has been approved',
      });
      yield this.initialize();
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while approving waiver',
      });
    }
  }

  *rejectWaiverItem(waiverItem, comment) {
    try {
      yield this.payoffService.rejectPayoffWaiverItem(this.loanId, this.payoffId, waiverItem.payoffWaiverItemId, comment);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Waiver item has been rejected',
      });
      yield this.initialize();
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while rejecting waiver',
      });
    }
  }

  *submitWaiverRequest() {
    try {
      yield this.payoffService.updatePayoffWaiverRequestStatus(this.waiverRequest?.payoffWaiverRequestId, this.documentStore?.currentDocument?.loanTaskId, this.documentStore?.currentDocument?.taskId, PENDING);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Waiver request has been submitted',
      });
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while submitting waiver request',
      });
    }
  }

  *markRequestAsResolved() {
    try {
      yield this.payoffService.updatePayoffWaiverRequestStatus(this.waiverRequest?.payoffWaiverRequestId, this.documentStore?.currentDocument?.loanTaskId, this.documentStore?.currentDocument?.taskId, APPROVED);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Waiver request has been updated',
      });
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while marking request as resolved',
      });
    }
  }

  setDisplayFields(data) {
    this.goodThroughDate = data?.goodThroughDate;
    this.reserveFees = data?.reserveFees;
    this.toChargesPrincipal = data?.toChargesPrincipal;
    this.waiverAmount = data?.waiverAmount;
    this.waiverDescription = data?.waiverDescription;
    this.payoffComments = data?.payoffComments;
    this.fundedAmount = data?.fundedAmount;
    this.totalExtensionFee = data?.totalExtensionFee;
    this.prepaymentPenalty = data?.prepaymentPenalty;
    this.otherFeesAdjustment = data?.otherFeesAdjustment;
    this.otherFeesCategories = data?.otherFeesCategories;
  }

  setInterest(waiverItems) {
    this.interestWaiverItems = waiverItems.filter(item => item.section === 'Interest');
  }

  setServicerFees(waiverItems) {
    this.servicerFeeWaiverItems = waiverItems.filter(item => item.section === 'Servicer Fees');
  }

  setUnpaidCharges(waiverItems) {
    this.unpaidChargeWaiverItems = waiverItems.filter(item => item.section === 'Unpaid Charges');
  }

  setFinalTotals() {
    try {
      this.sectionTotals = [
        {
          name: 'Unpaid Balance',
          amount: this.fundedAmount || 0,
        },
        {
          name: 'Payoff Summary',
          amount: this.getPayoffAmount(),
        },
        {
          name: 'Unpaid Interest',
          amount: this.getListTotal(this.interestWaiverItems),
        },
        {
          name: 'Servicer Fees',
          amount: this.getListTotal(this.servicerFeeWaiverItems),
        },
        {
          name: 'Unpaid Charges',
          amount: this.getListTotal(this.unpaidChargeWaiverItems),
        },
      ];
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while getting final totals.',
      });
    }
  }

  getPayoffAmount() {
    return (this.reserveFees || 0) + (this.toChargesPrincipal || 0)
      + (this.prepaymentPenalty || 0) + (this.waiverAmount || 0)
      + (this.otherFeesAdjustment || 0);
  }

  getListTotal(list) {
    return list.reduce((total, item) => total + (item.originalAmount || 0), 0);
  }

  calculateFinalTotal() {
    this.finalTotal = (this.fundedAmount || 0)
      + this.getPayoffAmount()
      + this.getListTotal(this.interestWaiverItems)
      + this.getListTotal(this.servicerFeeWaiverItems)
      + this.getListTotal(this.unpaidChargeWaiverItems);
  }
}

export default PayoffReconciliationReviewStore;